import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:7271/api';

// Types
export interface Reminder {
  id: number;
  userId: number;
  userName?: string;
  reminderType: number;
  reminderTypeName: string;
  title: string;
  description?: string;
  dueDateTime: string;
  entityType?: number;
  entityTypeName?: string;
  entityId?: number;
  entityName?: string;
  status: number;
  statusName: string;
  priority: number;
  priorityName: string;
  handledAt?: string;
  handledBy?: number;
  handledByName?: string;
  handledNotes?: string;
  isRecurring: boolean;
  recurrencePattern?: string;
  createdBy: number;
  createdByName: string;
  createdAt: string;
  updatedAt?: string;
  isOverdue: boolean;
}

export interface CreateReminderDto {
  userId: number;
  reminderType: number;
  title: string;
  description?: string;
  dueDateTime: string;
  entityType?: number;
  entityId?: number;
  priority: number;
  isRecurring: boolean;
  recurrencePattern?: string;
}

export interface UpdateReminderDto {
  title: string;
  description?: string;
  dueDateTime: string;
  priority: number;
  reminderType: number;
}

export interface HandleReminderDto {
  handledNotes?: string;
}

export interface PostponeReminderDto {
  newDueDate: string;
  reason?: string;
}

export interface ReminderFilterDto {
  status?: number;
  fromDate?: string;
  toDate?: string;
  priority?: number;
  reminderType?: number;
}

// Enums
export enum ReminderType {
  Call = 1,
  FollowUp = 2,
  Document = 3,
  Meeting = 4,
  Payment = 5,
  Custom = 6
}

export enum ReminderStatus {
  Pending = 1,
  Handled = 2,
  Postponed = 3,
  Cancelled = 4
}

export enum ReminderPriority {
  High = 1,
  Medium = 2,
  Low = 3
}

export enum EntityType {
  Lead = 1,
  Client = 2,
  Case = 3,
  General = 4
}

// API calls
export const reminderApi = {
  // Get reminders with filters
  getReminders: async (filter: ReminderFilterDto = {}): Promise<Reminder[]> => {
    const params = new URLSearchParams();
    if (filter.status !== undefined) params.append('status', filter.status.toString());
    if (filter.fromDate) params.append('fromDate', filter.fromDate);
    if (filter.toDate) params.append('toDate', filter.toDate);
    if (filter.priority !== undefined) params.append('priority', filter.priority.toString());
    if (filter.reminderType !== undefined) params.append('reminderType', filter.reminderType.toString());

    const response = await axios.get(`${API_BASE_URL}/reminders?${params.toString()}`);
    return response.data;
  },

  // Get today's reminders
  getTodayReminders: async (): Promise<Reminder[]> => {
    const response = await axios.get(`${API_BASE_URL}/reminders/today`);
    return response.data;
  },

  // Get overdue reminders
  getOverdueReminders: async (): Promise<Reminder[]> => {
    const response = await axios.get(`${API_BASE_URL}/reminders/overdue`);
    return response.data;
  },

  // Get upcoming reminders
  getUpcomingReminders: async (daysAhead: number = 7): Promise<Reminder[]> => {
    const response = await axios.get(`${API_BASE_URL}/reminders/upcoming?daysAhead=${daysAhead}`);
    return response.data;
  },

  // Get reminders for a specific client
  getClientReminders: async (clientId: number): Promise<Reminder[]> => {
    const response = await axios.get(`${API_BASE_URL}/reminders/client/${clientId}`);
    return response.data;
  },

  // Get reminder by ID
  getReminder: async (id: number): Promise<Reminder> => {
    const response = await axios.get(`${API_BASE_URL}/reminders/${id}`);
    return response.data;
  },

  // Create reminder
  create: async (reminder: CreateReminderDto): Promise<Reminder> => {
    const response = await axios.post(`${API_BASE_URL}/reminders`, reminder);
    return response.data;
  },
  
  // Alias for backward compatibility
  createReminder: async (reminder: CreateReminderDto): Promise<Reminder> => {
    const response = await axios.post(`${API_BASE_URL}/reminders`, reminder);
    return response.data;
  },

  // Update reminder
  updateReminder: async (id: number, reminder: UpdateReminderDto): Promise<Reminder> => {
    const response = await axios.put(`${API_BASE_URL}/reminders/${id}`, reminder);
    return response.data;
  },

  // Delete reminder
  deleteReminder: async (id: number): Promise<void> => {
    await axios.delete(`${API_BASE_URL}/reminders/${id}`);
  },

  // Handle reminder
  handleReminder: async (id: number, data: HandleReminderDto): Promise<Reminder> => {
    const response = await axios.post(`${API_BASE_URL}/reminders/${id}/handle`, data);
    return response.data;
  },

  // Postpone reminder
  postponeReminder: async (id: number, data: PostponeReminderDto): Promise<Reminder> => {
    const response = await axios.post(`${API_BASE_URL}/reminders/${id}/postpone`, data);
    return response.data;
  }
};

// Helper functions
export const getReminderTypeLabel = (type: ReminderType): string => {
  switch (type) {
    case ReminderType.Call: return 'שיחה';
    case ReminderType.FollowUp: return 'מעקב';
    case ReminderType.Document: return 'מסמך';
    case ReminderType.Meeting: return 'פגישה';
    case ReminderType.Payment: return 'תשלום';
    case ReminderType.Custom: return 'אחר';
    default: return 'לא ידוע';
  }
};

export const getReminderTypeIcon = (type: ReminderType): string => {
  switch (type) {
    case ReminderType.Call: return '📞';
    case ReminderType.FollowUp: return '🔄';
    case ReminderType.Document: return '📄';
    case ReminderType.Meeting: return '🤝';
    case ReminderType.Payment: return '💰';
    case ReminderType.Custom: return '📌';
    default: return '❓';
  }
};

export const getReminderPriorityColor = (priority: ReminderPriority): string => {
  switch (priority) {
    case ReminderPriority.High: return 'var(--error-color)';
    case ReminderPriority.Medium: return 'var(--warning-color)';
    case ReminderPriority.Low: return 'var(--success-color)';
    default: return 'var(--text-secondary)';
  }
};

export const formatReminderDate = (dateString: string): string => {
  const date = new Date(dateString);
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);

  // Check if today
  if (date.toDateString() === today.toDateString()) {
    return `היום ${date.toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' })}`;
  }
  
  // Check if tomorrow
  if (date.toDateString() === tomorrow.toDateString()) {
    return `מחר ${date.toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' })}`;
  }
  
  // Otherwise show full date
  return date.toLocaleString('he-IL', { 
    day: '2-digit', 
    month: '2-digit', 
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

export const handleApiError = (error: any): string => {
  if (error.response) {
    return error.response.data || 'שגיאה בביצוע הפעולה';
  } else if (error.request) {
    return 'שגיאה בחיבור לשרת';
  } else {
    return 'שגיאה לא צפויה';
  }
};

export default reminderApi;